import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  SubcarrierDeductionTypeUpdate,
  SubcarrierService,
  SubcarrierTrailerTypeContractUpdate,
} from '~services/subcarrier.service';
import { LmoTruckingVendorsService } from '~lmo/services';
import { TruckingVendor, TruckingVendorContract } from '~lmo/models/trucking-vendor.model';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import {
  BrokerTrailerType,
  ContractDeduction,
  ContractDeductionType,
  ContractDeductionTypeForm,
  Subcarrier,
  SubcarrierVendorContracts,
} from '~models/subcarrier.model';
import { distinctUntilChanged, map, skipWhile, startWith, take } from 'rxjs/operators';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { fuse } from '~utilities/fuse';
import { MatSnackBar } from '@angular/material/snack-bar';

const searchOptions: Fuse.FuseOptions<TruckingVendorContract> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 16,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.1,
};

interface FormValue {
  brokerage: TruckingVendor;
  brokerSubcarrier: Subcarrier;
  mainContract?: TruckingVendorContract;
  trailerTypeContracts: TruckingVendorContract[];
  contractDeductionTypes?: ContractDeductionTypeForm[];
}

@Component({
  selector: 'sa-subcarrier-vendor-contract-create-edit',
  templateUrl: './subcarrier-vendor-contract-create-edit.component.html',
  styleUrls: ['./subcarrier-vendor-contract-create-edit.component.scss'],
})
export class SubcarrierVendorContractCreateEditComponent implements OnInit, OnDestroy {
  @Input() public editContracts: SubcarrierVendorContracts;
  @Output() public onCloseClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  public allBrokerages$: Observable<TruckingVendor[]>;
  public brokeragesWithContracts$: Observable<TruckingVendor[]>;
  public brokerageOptions$: Observable<TruckingVendor[]>;
  public brokerSubcarriers$: Observable<Subcarrier[]>;
  public brokerContracts$: Observable<TruckingVendorContract[]>;
  public mainContractOptions$: Observable<TruckingVendorContract[]>;
  public billableTrailerContractOptions$: Observable<TruckingVendorContract[]>;
  public nonbillableTrailerContractOptions$: Observable<TruckingVendorContract[]>;

  public subcarrierContractsForm: FormGroup;

  public brokerFilter = new FormControl();
  public subcarrierFilter = new FormControl();
  public mainContractFilter = new FormControl();
  public contractFilters: FormControl[] = [];
  public contractOptions: Observable<TruckingVendorContract[]>[] = [];

  private subscriptions: Subscription[] = [];

  public networkActive$$ = new BehaviorSubject<boolean>(false);

  public isEdit: boolean;

  public brokerTrailerTypes$: Observable<BrokerTrailerType[]>;
  public contractDeductionTypes$: Observable<ContractDeductionType[]>;

  constructor(
    private subcarrierService: SubcarrierService,
    private lmoTruckingVendorService: LmoTruckingVendorsService,
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.subcarrierService.getSubcarriersForLMO();

    this.brokerTrailerTypes$ = this.subcarrierService.brokerTrailerTypes$;
    this.contractDeductionTypes$ = this.subcarrierService.contractDeductionTypes$;

    this.isEdit = !!this.editContracts?.id;

    this.allBrokerages$ = this.lmoTruckingVendorService.vendors$.pipe(
      map((vendors) => {
        if (!vendors) {
          return [];
        }

        return vendors.filter((vendor) => vendor.isBrokerage);
      }),
    );

    this.brokeragesWithContracts$ = combineLatest([
      this.allBrokerages$,
      this.lmoTruckingVendorService.allContracts$,
    ]).pipe(
      map(([brokerages, contracts]) => {
        if (!brokerages || !contracts) {
          return [];
        }

        return brokerages.filter((brokerage) => {
          return contracts.some((contract) => contract.vendorId === brokerage.id && !contract.archived);
        });
      }),
    );

    if (this.isEdit) {
      this.setupEditForm();
    } else {
      this.setupNewForm();
    }
  }

  private makeTrailerTypeControls(trailerTypes: BrokerTrailerType[]): FormArray {
    const trailerTypeControls = this.fb.array([]);

    trailerTypes.forEach(() => {
      trailerTypeControls.push(this.fb.control(null, []));
      this.contractFilters.push(new FormControl());
    });

    return trailerTypeControls;
  }

  private makeDeductionTypeControls(
    contractDeductionTypes: ContractDeductionType[],
    contractDeduction: Record<number, ContractDeduction> | null,
  ): FormArray {
    const contractDeductionTypeControls = this.fb.array([]);

    contractDeductionTypes.forEach((type) => {
      const deduction = contractDeduction?.[type.id];
      const dType = deduction?.deductionFlatRateCents ? 'flat' : 'percentage';
      const dAmount = deduction?.deductionFlatRateCents ? deduction?.deductionFlatRate : deduction?.deductionPercentage;

      contractDeductionTypeControls.push(
        this.fb.group({
          deductionType: [deduction ? dType : null, []],
          deductionAmount: [deduction ? dAmount : null, []],
        }),
      );
    });
    return contractDeductionTypeControls;
  }

  private async setupNewForm() {
    const trailerTypes = await this.brokerTrailerTypes$.pipe(take(1)).toPromise();
    const contractDeductionTypes = await this.contractDeductionTypes$.pipe(take(1)).toPromise();

    const trailerTypeControls = this.makeTrailerTypeControls(trailerTypes);
    const contractDeductionTypeControls = this.makeDeductionTypeControls(contractDeductionTypes, null);

    this.subcarrierContractsForm = this.fb.group({
      brokerage: [{ value: null, disabled: this.isEdit }, [Validators.required]],
      brokerSubcarrier: [{ value: null, disabled: this.isEdit }, [Validators.required]],
      mainContract: [null, []],
      trailerTypeContracts: trailerTypeControls,
      contractDeductionTypes: contractDeductionTypeControls,
    });

    this.subscriptions.push(
      this.subcarrierContractsForm
        .get('brokerage')
        .valueChanges.pipe(startWith(null), distinctUntilChanged())
        .subscribe((brokerage) => {
          if (!brokerage) {
            this.subcarrierContractsForm.get('brokerSubcarrier').disable();
            this.subcarrierContractsForm.get('mainContract').disable();

            trailerTypeControls.controls.forEach((control) => {
              control.disable();
            });
            contractDeductionTypeControls.controls.forEach((control) => {
              control.disable();
            });
          } else {
            this.subcarrierContractsForm.get('brokerSubcarrier').enable();
            this.subcarrierContractsForm.get('mainContract').enable();

            trailerTypeControls.controls.forEach((control) => {
              control.enable();
            });
            contractDeductionTypeControls.controls.forEach((control) => {
              control.enable();
            });
          }
        }),
    );

    this.brokerageOptions$ = this.brokeragesWithContracts$;

    this.brokerSubcarriers$ = combineLatest([
      this.subcarrierService.subcarriersForLMO$,
      this.subcarrierService.subcarrierVendorContracts$,
      this.subcarrierContractsForm.get('brokerage').valueChanges.pipe(startWith(null)),
    ]).pipe(
      map(([subcarriers, subcarrierContracts, brokerage]) => {
        if (!subcarriers || !brokerage) {
          return [];
        }

        const nonExistingSubcarriers = subcarriers.filter((subcarrier) => {
          return !subcarrierContracts.some((contract) => contract.subcarrierId === subcarrier.id);
        });

        return nonExistingSubcarriers.filter((subcarrier) => subcarrier.brokerId === brokerage?.id);
      }),
    );

    this.brokerContracts$ = combineLatest([
      this.lmoTruckingVendorService.allContracts$,
      this.subcarrierContractsForm.get('brokerage').valueChanges.pipe(startWith(null)),
    ]).pipe(
      map(([contracts, brokerage]) => {
        if (!contracts || !brokerage) {
          return [];
        }

        return contracts.filter((contract) => contract.vendorId === brokerage?.id && !contract.archived);
      }),
    );

    this.mainContractOptions$ = combineLatest([
      this.brokerContracts$.pipe(map(fuse<TruckingVendorContract>(searchOptions))),
      this.mainContractFilter.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, filter]) => {
        if (!fused) {
          return [];
        }

        if (!filter || filter === '') {
          return fused.data;
        }

        return fused.fuse.search(filter) as any;
      }),
    );

    for (let i = 0; i < trailerTypes.length; i++) {
      this.contractOptions.push(
        combineLatest([
          this.brokerContracts$.pipe(map(fuse<TruckingVendorContract>(searchOptions))),
          this.contractFilters[i].valueChanges.pipe(startWith('')),
        ]).pipe(
          map(([fused, filter]) => {
            if (!fused) {
              return [];
            }

            if (!filter || filter === '') {
              return fused.data;
            }

            return fused.fuse.search(filter) as any;
          }),
        ),
      );
    }
  }

  private async setupEditForm() {
    // get current broker and subcarrier
    const brokerList = await this.allBrokerages$
      .pipe(
        skipWhile((brokers) => !brokers || brokers.length === 0),
        take(1),
      )
      .toPromise();
    const brokerage = brokerList.find((broker) => broker.id === this.editContracts.brokerId);

    this.brokerageOptions$ = this.allBrokerages$;

    this.brokerSubcarriers$ = this.subcarrierService.subcarriersForLMO$.pipe(
      map((subcarriers) => {
        if (!subcarriers || !brokerage) {
          return [];
        }

        return subcarriers.filter((sc) => sc.brokerId === brokerage?.id);
      }),
    );

    const subcarrierList = await this.brokerSubcarriers$
      .pipe(
        skipWhile((subcarriers) => !subcarriers || subcarriers.length === 0),
        take(1),
      )
      .toPromise();
    const subcarrier = subcarrierList.find((sc) => sc.id === this.editContracts.subcarrierId);

    this.brokerContracts$ = this.lmoTruckingVendorService.allContracts$.pipe(
      map((contracts) => {
        if (!contracts) {
          return [];
        }

        return contracts.filter((contract) => contract.vendorId === brokerage?.id && !contract.archived);
      }),
    );

    const trailerTypes = await this.brokerTrailerTypes$.pipe(take(1)).toPromise();
    const contractDeductionTypes = await this.contractDeductionTypes$.pipe(take(1)).toPromise();

    const trailerTypeControls = this.makeTrailerTypeControls(trailerTypes);
    const contractDeductionTypeControls = this.makeDeductionTypeControls(
      contractDeductionTypes,
      this.editContracts.contractDeductions,
    );

    this.subcarrierContractsForm = this.fb.group({
      brokerage: [{ value: brokerage, disabled: true }, [Validators.required]],
      brokerSubcarrier: [{ value: subcarrier, disabled: true }, [Validators.required]],
      mainContract: [null, []],
      trailerTypeContracts: trailerTypeControls,
      contractDeductionTypes: contractDeductionTypeControls,
    });

    this.mainContractOptions$ = combineLatest([
      this.brokerContracts$.pipe(map(fuse<TruckingVendorContract>(searchOptions))),
      this.mainContractFilter.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, filter]) => {
        if (!fused) {
          return [];
        }

        if (!filter || filter === '') {
          return fused.data;
        }

        return fused.fuse.search(filter) as any;
      }),
    );

    for (let i = 0; i < trailerTypes.length; i++) {
      this.contractOptions.push(
        combineLatest([
          this.brokerContracts$.pipe(map(fuse<TruckingVendorContract>(searchOptions))),
          this.contractFilters[i].valueChanges.pipe(startWith('')),
        ]).pipe(
          map(([fused, filter]) => {
            if (!fused) {
              return [];
            }

            if (!filter || filter === '') {
              return fused.data;
            }

            return fused.fuse.search(filter) as any;
          }),
        ),
      );
    }

    this.subscriptions.push(
      this.brokerContracts$.subscribe((contracts) => {
        if (!contracts) {
          return;
        }

        const mainContract = contracts.find((contract) => contract.id === this.editContracts.mainContractId);

        const trailerTypeContracts: TruckingVendorContract[] = [];

        for (let i = 0; i < trailerTypes.length; i++) {
          const trailerType = trailerTypes[i];
          const contractId = this.editContracts.trailerTypeContracts?.[trailerType.id]?.contractId;
          const trailerTypeContract = contracts.find((contract) => contract.id === contractId);
          trailerTypeContracts.push(trailerTypeContract);
        }

        this.subcarrierContractsForm.patchValue({
          mainContract,
          trailerTypeContracts,
        });
      }),
    );
  }

  public compareById(o1, o2) {
    return o1?.id === o2?.id;
  }

  onDeductionTypeChange(index: number) {
    const deductionFormArray = this.subcarrierContractsForm.get('contractDeductionTypes') as FormArray;
    deductionFormArray
      .at(index)
      .get('deductionAmount')
      .reset();
  }

  getDeductionType(index: number): string {
    const deductionTypeFormArray = this.subcarrierContractsForm.get('contractDeductionTypes') as FormArray;
    return deductionTypeFormArray.at(index).get('deductionType').value;
  }

  closeClicked(close) {
    this.onCloseClicked.emit(close);
  }

  async updateSubcarrierContracts() {
    try {
      this.networkActive$$.next(true);

      const trailerTypes = await this.brokerTrailerTypes$.pipe(take(1)).toPromise();
      const contractDeductionTypes = await this.contractDeductionTypes$.pipe(take(1)).toPromise();

      const formValue = this.subcarrierContractsForm.getRawValue() as FormValue;

      const trailerTypeUpdates: SubcarrierTrailerTypeContractUpdate[] = [];

      for (let i = 0; i < trailerTypes.length; i++) {
        const trailerType = trailerTypes[i];
        const contractId = formValue?.trailerTypeContracts?.[i]?.id;

        if (contractId) {
          trailerTypeUpdates.push({
            trailerTypeId: trailerType.id,
            contractId,
          });
        }
      }

      const contractDeductionTypeUpdates: SubcarrierDeductionTypeUpdate[] = [];

      for (let i = 0; i < contractDeductionTypes.length; i++) {
        const contractDeductionType = contractDeductionTypes[i];
        const deductionAmount = formValue?.contractDeductionTypes?.[i]?.deductionAmount;
        const deductionType = formValue?.contractDeductionTypes?.[i]?.deductionType;

        if (deductionAmount && deductionType) {
          contractDeductionTypeUpdates.push({
            contractDeductionTypeId: contractDeductionType.id,
            contractDeductionTypeValue: deductionAmount,
            deductionType: deductionType,
          });
        } else if (!deductionAmount && deductionType) {
          this.snackbar.open('Missing Deduction amount', null, { duration: 3000, panelClass: ['snackbar-error'] });
          return;
        } else if (deductionAmount && !deductionType) {
          this.snackbar.open('Missing Deduction type', null, { duration: 3000, panelClass: ['snackbar-error'] });
          return;
        }
      }

      const update = {
        subcarrierId: formValue.brokerSubcarrier.id,
        mainContractId: formValue.mainContract?.id,
        trailerTypeContracts: trailerTypeUpdates,
        contractDeductionTypes: contractDeductionTypeUpdates,
      };

      const result = await this.subcarrierService.updateSubcarrierVendorContracts(update);

      if (result) {
        const message = this.isEdit ? 'Updated subcarrier contracts' : 'Added subcarrier contracts';

        this.snackbar.open(message, null, { duration: 3000 });
        this.closeClicked(true);
      } else {
        const message = this.isEdit ? 'Failed to update subcarrier contracts' : 'Failed to add subcarrier contracts';
        this.snackbar.open(message, null, { duration: 3000, panelClass: ['snackbar-error'] });
      }
    } finally {
      this.networkActive$$.next(false);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
